// ================================================================================================
import { Component, Fragment } from 'react';
import _ from "lodash";
import { DebugEx } from "@Utils/DebugEx";
import { isDefined, isFunction } from "@Utils/Micro";
import { DialogConf, ExDialog } from '@Primitive/ExDialog';
import { Localization, LocalizationDefaultKey } from '@Primitive/Localization/Localization';
import { SVGIconSelector, YodiwoSVG } from '@UI/Icons/SvgIconsSelector';
import { ToolbarIconButton } from '@Primitive/ToolbarIconButton';
import { IconButton, Button } from '@Primitive/Button';
import { red200, grey600, blue200, grey50 } from '@Utils/Colors';
import Checkbox from '@Primitive/Checkbox';
import Toolbar from '@Primitive/Toolbar';
import TextBox from '@Primitive/TextBox';
import { FlexboxGrid, Panel } from 'rsuite';
import { Select, SelectOptions, SelectOption } from '@Primitive/Select';
import Tabs from '@Primitive/Tabs/Tabs';
// ================================================================================================
export interface LocalizationManagementProps {
    width: number;
    height: number;
    NodeKey: string;
    PagesInfo: { [key: string]: { Name: string, key: string } };
    onCloseRequest: () => void;
    onSaveRequest: () => void;
    toolbarHeight: number;
}
// ------------------------------------------------------------------------------------------------
export interface LocalizationManagementState {
    dialogConf: DialogConf;
    selectedFlag: string;
    newLanguageCode: string;
    selectedLanguageCode: string;
}
// ------------------------------------------------------------------------------------------------
export class LocalizationManagement extends Component<LocalizationManagementProps, LocalizationManagementState>{
    private localization: Localization;
    // -------------------------------------------------------------------------------------------
    constructor(props) {
        super(props);

        // Link with localization
        this.localization = _.cloneDeep(Localization.GetInstance(this.props.NodeKey));
        if (!isDefined(this.localization))
            DebugEx.TraceError("Failed to find the localization", this.props.NodeKey);

        let defaultLang = this.localization.DefaultLanguage;
        let info = this.localization.GetSupportedLanguages();
        let otherLang = _.find(info, (i) => i.Code != defaultLang);

        this.state = {
            dialogConf: null,
            selectedFlag: "",
            newLanguageCode: "",
            selectedLanguageCode: (otherLang) ? otherLang.Code : ""
        };
    }
    // -------------------------------------------------------------------------------------------
    renderLanguageTable() {
        let info = this.localization.GetSupportedLanguages();
        return (
            <FlexboxGrid>
                <FlexboxGrid.Item className='y-flexbox-grid-item table-cell' colspan={6}>Code</FlexboxGrid.Item>
                <FlexboxGrid.Item className='y-flexbox-grid-item table-cell' colspan={6}>Label</FlexboxGrid.Item>
                <FlexboxGrid.Item className='y-flexbox-grid-item table-cell' colspan={4}>Flag Icon</FlexboxGrid.Item>
                <FlexboxGrid.Item className='y-flexbox-grid-item table-cell' colspan={4}>Enabled</FlexboxGrid.Item>
                <FlexboxGrid.Item className='y-flexbox-grid-item table-cell' colspan={2}>Default</FlexboxGrid.Item>
                <FlexboxGrid.Item className='y-flexbox-grid-item table-cell' colspan={2}>Delete</FlexboxGrid.Item>

                {_.map(info, (l, i: string) => <Fragment key={`el-${i}`}>
                    <FlexboxGrid.Item
                        key={`code-${i}`}
                        className='y-flexbox-grid-item table-cell'
                        colspan={6}>
                        {l.Code}
                    </FlexboxGrid.Item>

                    <FlexboxGrid.Item
                        key={`label-${i}`}
                        className='y-flexbox-grid-item table-cell'
                        colspan={6}>
                        <TextBox
                            fullWidth
                            type='text'
                            value={l.Label}
                            onChangeString={value => {
                                l.Label = value;
                                this.localization.SetLanguageInfo(l);
                                this.forceUpdate();
                            }}
                        />
                    </FlexboxGrid.Item>

                    <FlexboxGrid.Item
                        key={`flag-${i}`}
                        className='y-flexbox-grid-item table-cell'
                        colspan={4}>
                        <div
                            style={{
                                width: 35,
                                height: 35,
                                cursor: 'pointer',
                                margin: '0 auto'
                            }}
                            onClick={() => {
                                this.setState({
                                    selectedFlag: l.FlagIcon,
                                    dialogConf: {
                                        title: 'Select Flag',
                                        contentStaticSize: { w: 0.65, h: 0.9 },
                                        actions: [
                                            <Button
                                                key='close'
                                                height='100%'
                                                label='Close'
                                                borderRadius={0}
                                                shadowLevel={0}
                                                style={{ background: 'transparent', color: red200 }}
                                                onClick={() => this.setState({ dialogConf: null })} />,
                                            <Button
                                                key='save'
                                                height='100%'
                                                label='Save'
                                                borderRadius={0}
                                                shadowLevel={0}
                                                style={{ background: 'transparent', color: blue200 }}
                                                onClick={() => {
                                                    l.FlagIcon = this.state.selectedFlag;
                                                    this.localization.SetLanguageInfo(l);
                                                    this.setState({ dialogConf: null });
                                                }} />
                                        ],
                                        bodyFunc: (w, h) => <SVGIconSelector
                                            width={w}
                                            height={h}
                                            defaultIcon={l.FlagIcon}
                                            packages={['flags']}
                                            onIconSelection={(uri) => this.setState({ selectedFlag: uri })} />
                                    }
                                })
                            }}>
                            <YodiwoSVG uri={l.FlagIcon} width={35} height={35} />
                        </div>
                    </FlexboxGrid.Item>

                    <FlexboxGrid.Item
                        key={`enabled-${i}`}
                        className='y-flexbox-grid-item table-cell'
                        colspan={4}>
                        <Checkbox
                            value={l.IsEnabled}
                            onClick={l.Code == this.localization.DefaultLanguage ? undefined : () => {
                                l.IsEnabled = !l.IsEnabled;
                                this.localization.SetLanguageInfo(l);
                                this.forceUpdate();
                            }}
                        />
                    </FlexboxGrid.Item>

                    <FlexboxGrid.Item
                        key={`default-${i}`}
                        className='y-flexbox-grid-item table-cell'
                        colspan={2}>
                        <Checkbox
                            checkedIcon='fas fa-dot-circle'
                            uncheckedIcon='far fa-circle'
                            value={l.IsDefault}
                            onClick={() => {
                                // disable all other languages 
                                let locInfo = this.localization.GetSupportedLanguages();
                                for (var info in locInfo) {
                                    locInfo[info].IsDefault = false;
                                    this.localization.SetLanguageInfo(locInfo[info]);
                                }

                                // set as default the pressed one
                                l.IsDefault = true;
                                this.localization.SetLanguageInfo(l);
                                this.forceUpdate();
                            }} />
                    </FlexboxGrid.Item>

                    <FlexboxGrid.Item
                        key={`delete-${i}`}
                        className='y-flexbox-grid-item table-cell'
                        colspan={2}>
                        <IconButton
                            iconClassName='fas fa-trash'
                            iconStyle={{ color: grey600 }}
                            disabled={l.Code == this.localization.DefaultLanguage}
                            onClick={() => {
                                this.localization.DeleteLanguage(l.Code);
                                this.forceUpdate();
                            }} />
                    </FlexboxGrid.Item>

                </Fragment>
                )}
            </FlexboxGrid>
        );
    }
    // -------------------------------------------------------------------------------------------
    renderTranslation(height: number) {
        const widgetsKey = this.localization.GetLanguageGroups(this.localization.DefaultLanguage);
        const groups = _.groupBy(widgetsKey, (k) => {
            let info = Localization.TryGetPageInfo(k);
            if (isDefined(info))
                return info.Name;
            return _.startsWith(k, "Primitive") ? "Primitive" : k.substr(0, 11)
        });
        const info = this.localization.GetSupportedLanguages();
        const infoKeys = _.filter(_.keys(info), (k) => k != this.localization.DefaultLanguage);
        if (infoKeys.length == 0)
            return null;

        const infoDefaultLang = info[this.localization.DefaultLanguage];
        let infoLang = info[this.state.selectedLanguageCode];
        if (!isDefined(infoLang)) {
            this.setState({ selectedLanguageCode: infoKeys[0] });
            infoLang = info[infoKeys[0]];
        }
        const style = {
            marginRight: 10,
            display: "inline-block"
        };
        return <div style={{ height: height }}>
            <FlexboxGrid>
                <FlexboxGrid.Item className='y-flexbox-grid-item table-cell left' colspan={12}>
                    <div style={style}>
                        <YodiwoSVG uri={infoDefaultLang.FlagIcon} width={20} height={20} />
                    </div>
                    {`${infoDefaultLang.Label} (${infoDefaultLang.Code})`}
                </FlexboxGrid.Item>

                <FlexboxGrid.Item className='y-flexbox-grid-item table-cell left' colspan={12}>
                    <div style={style}>
                        <YodiwoSVG uri={infoLang.FlagIcon} width={20} height={20} />
                    </div>
                    {`${infoLang.Label} (${infoLang.Code})`}
                </FlexboxGrid.Item>
            </FlexboxGrid>

            {_.map(groups, (g, gkey) => {
                if (g.length == 0) return null;

                let gItems = _.map(g, (groupKey, i) => {
                    let textDefaultMap = this.localization.GetSpecificTextMap(infoDefaultLang.Code, groupKey, false);
                    let textTransMap = this.localization.GetSpecificTextMap(infoLang.Code, groupKey, (infoLang.Code == LocalizationDefaultKey) ? true : false);
                    let subCategoryName = groupKey;
                    if (groupKey == "Primitive_DashboardManager")
                        subCategoryName = "Page List";

                    return <Panel
                        collapsible
                        key={groupKey + "-" + i}
                        style={{
                            backgroundColor: "rgb(91, 97, 99)",
                            color: grey50,
                            borderRadius: 0
                        }}
                        header={<div style={{ color: grey50 }}>
                            <div>{subCategoryName}</div>
                            <small>{groupKey}</small>
                        </div>}>
                        <FlexboxGrid>
                            {_.map(textDefaultMap, (text, key) => <Fragment key={`el-${i}`}>
                                <FlexboxGrid.Item className='y-flexbox-grid-item table-cell left' colspan={12}>{text}</FlexboxGrid.Item>
                                <FlexboxGrid.Item className='y-flexbox-grid-item table-cell left' colspan={12}>
                                    <TextBox
                                        fullWidth
                                        type='text'
                                        prefix={text}
                                        value={textTransMap[key] ? textTransMap[key] : ""}
                                        onChangeString={value => {
                                            this.localization.AddTranslateText(infoLang.Code, groupKey, key, value);
                                            this.forceUpdate();
                                        }} />
                                </FlexboxGrid.Item>
                            </Fragment>)}
                        </FlexboxGrid>
                    </Panel>;
                });

                // TODO: get info for page
                let pageInfo = this.props.PagesInfo[gkey];
                if (!isDefined(pageInfo)) {
                    // get page info from `Localization`
                    pageInfo = Localization.TryGetPageInfo(gkey);
                    if (!isDefined(pageInfo))
                        // fallback; just add a dummy description
                        pageInfo = { Name: "", key: gkey };
                }

                return <Panel
                    collapsible
                    key={gkey}
                    style={{
                        backgroundColor: "rgb(91, 97, 99)",
                        color: grey50,
                        borderRadius: 0
                    }}
                    header={<div style={{ color: grey50 }}>
                        <div>{pageInfo.Name}</div>
                        <small>{pageInfo.key}</small>
                    </div>}>
                    {gItems}
                </Panel>;
            })}
        </div>
    }
    // -------------------------------------------------------------------------------------------
    private dialogOnRefuse = () => {
        if (isFunction(this.props.onCloseRequest))
            this.props.onCloseRequest();
    }
    // -------------------------------------------------------------------------------------------
    private dialogOnAccept = () => {
        try {
            const origLangSystem = Localization.GetInstance(this.props.NodeKey);
            const infoOrig = origLangSystem.GetSupportedLanguages();
            const info = this.localization.GetSupportedLanguages();

            // Find deleted languages and delete them from the global system
            _.forOwn(infoOrig, (o, key) => {
                if (!_.has(info, key)) {
                    origLangSystem.DeleteLanguage(key);
                }
            });

            // Update default language info
            origLangSystem.SetLanguageInfo(info[this.localization.DefaultLanguage]);

            const infoKeys = _.keys(info);
            if (infoKeys.length > 0) {
                // Update original system with updated language
                _.forEach(infoKeys, (k) => {
                    origLangSystem.UpdateTranslatedLanguage(k, this.localization.GetLanguageDictionary(k));
                });
            }

            // Set the flag that we have pending data
            origLangSystem.PendingSaveChanges = true;

            if (isFunction(this.props.onSaveRequest))
                this.props.onSaveRequest();
        } catch (ex) {
            DebugEx.TraceCriticalException(ex);
        }
    }
    // -------------------------------------------------------------------------------------------
    render() {
        const tabHeight = 40;
        const { toolbarHeight } = this.props;
        let height = this.props.height - toolbarHeight - tabHeight;
        let transLangs = this.localization.GetSupportedLanguages();
        return <div style={{ width: this.props.width, height: this.props.height }}>
            <Toolbar
                title='Localization Settings'
                style={{
                    zIndex: 1000,
                    position: "relative",
                    boxShadow: 'rgba(0, 0, 0, 0.16) 0px 3px 10px, rgba(0, 0, 0, 0.23) 0px 3px 10px'
                }}
                rightItems={[
                    <ToolbarIconButton
                        key='save'
                        tooltip='Save'
                        className='fas fa-cloud-upload-alt dialog-button'
                        style={{ fontSize: 16 }}
                        onClick={this.dialogOnAccept}
                    />,
                    <ToolbarIconButton
                        key='cancel'
                        tooltip='Cancel'
                        className='fas fa-times dialog-button'
                        style={{ fontSize: 16, color: red200 }}
                        onClick={this.dialogOnRefuse}
                    />
                ]} />
            <Tabs
                style={{
                    zIndex: 2,
                    height: tabHeight,
                    position: 'relative',
                    color: grey50,
                    backgroundColor: 'rgb(91, 97, 99)'
                }}
                items={[{
                    key: 'settings',
                    label: 'Settings',
                    content: (
                        <div>
                            <Toolbar
                                title={null}
                                style={{
                                    height: tabHeight,
                                    backgroundColor: 'rgb(0, 157, 119)'
                                }}
                                rightItems={[<IconButton
                                    key='add'
                                    tooltip='Add'
                                    iconClassName='fas fa-plus'
                                    iconStyle={{ color: 'white' }}
                                    onClick={() => this.setState({
                                        dialogConf: {
                                            title: 'Language Code',
                                            contentStaticSize: { w: 0.4, h: 0.5 },
                                            actions: [
                                                <Button
                                                    key='close'
                                                    height='100%'
                                                    label='Close'
                                                    borderRadius={0}
                                                    shadowLevel={0}
                                                    style={{ background: 'transparent', color: red200 }}
                                                    onClick={() => this.setState({ dialogConf: null })} />,
                                                <Button
                                                    key='add'
                                                    height='100%'
                                                    label='Add'
                                                    borderRadius={0}
                                                    shadowLevel={0}
                                                    style={{ background: 'transparent', color: blue200 }}
                                                    onClick={() => {
                                                        this.localization.SetLanguageInfo({
                                                            Code: this.state.newLanguageCode,
                                                            Label: this.state.newLanguageCode,
                                                            FlagIcon: '',
                                                            IsEnabled: true,
                                                            IsDefault: false
                                                        });
                                                        this.setState({ dialogConf: null });
                                                    }} />
                                            ],
                                            bodyFunc: (w, h) => <div style={{ width: w, height: h, padding: 20 }}>
                                                <TextBox
                                                    fullWidth
                                                    type='text'
                                                    prefix='Code'
                                                    onChangeString={(value: string) => this.setState({ newLanguageCode: value })} />
                                            </div>
                                        }
                                    })} />]}
                            />
                            {this.renderLanguageTable()}
                        </div>
                    )
                },
                {
                    key: 'translation',
                    label: 'Translation',
                    content: (
                        <div>
                            <Toolbar
                                title={null}
                                style={{
                                    height: tabHeight,
                                    backgroundColor: 'rgb(0, 157, 119)'
                                }}
                                rightItems={[<Select
                                    key='select-lang'
                                    isMulti={false}
                                    isClearable={false}
                                    options={_.map(transLangs, x => ({
                                        label: `${x.Label} (${x.Code})`,
                                        value: x.Code
                                    } as SelectOption)) as SelectOptions}
                                    valueStr={this.state.selectedLanguageCode}
                                    onChange={value => this.setState({ selectedLanguageCode: value })} />]} />
                            {this.renderTranslation(height - tabHeight)}
                        </div>
                    )
                }]} />
            <ExDialog
                key="dialog"
                Enable={this.state.dialogConf != null}
                Conf={this.state.dialogConf} />
        </div>;
    }
    // -------------------------------------------------------------------------------------------
}
// ================================================================================================